import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react'
import type { CSSProperties } from 'react'
import { forwardRef, useCallback, useId } from 'react'

export interface RankFieldProps {
  value: string[]
  options: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  fieldId: string
}

export const RankField = forwardRef<HTMLDivElement, RankFieldProps>(function RankField(
  { value, options, onChange, disabled, fieldId },
  ref,
) {
  const dndId = useId()

  // If value is empty or shorter than options, fill from options
  const items = value.length > 0 ? reconcileItems(value, options) : options

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = items.indexOf(active.id as string)
      const newIndex = items.indexOf(over.id as string)
      if (oldIndex === -1 || newIndex === -1) return

      onChange(arrayMove(items, oldIndex, newIndex))
    },
    [items, onChange],
  )

  const moveItem = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= items.length) return
      onChange(arrayMove(items, index, targetIndex))
    },
    [items, onChange],
  )

  return (
    <DndContext id={dndId} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div
          ref={ref}
          tabIndex={-1}
          role="list"
          aria-label={`Rank items for ${fieldId}`}
          className="space-y-1 focus:outline-none"
        >
          {items.map((item, index) => (
            <SortableRankItem
              key={item}
              item={item}
              index={index}
              total={items.length}
              disabled={disabled}
              onMoveUp={() => moveItem(index, 'up')}
              onMoveDown={() => moveItem(index, 'down')}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
})

interface SortableRankItemProps {
  item: string
  index: number
  total: number
  disabled?: boolean
  onMoveUp: () => void
  onMoveDown: () => void
}

function SortableRankItem({ item, index, total, disabled, onMoveUp, onMoveDown }: SortableRankItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item,
    disabled,
  })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="listitem"
      className="flex items-center gap-2 bg-surface-soft rounded-lg px-3 py-2 text-sm"
    >
      <button
        type="button"
        className="-my-2 -ml-1 flex h-10 w-8 cursor-grab items-center justify-center text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        aria-label={`Drag ${item}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="w-6 text-center font-medium text-muted-foreground">{index + 1}</span>
      <span className="flex-1">{item}</span>
      <button
        type="button"
        onClick={onMoveUp}
        disabled={disabled || index === 0}
        aria-label={`Move ${item} up`}
        className="-my-2 flex h-10 w-9 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronUp className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={disabled || index === total - 1}
        aria-label={`Move ${item} down`}
        className="-my-2 flex h-10 w-9 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronDown className="w-4 h-4" />
      </button>
    </div>
  )
}

/** Reconcile value with options: keep value order, append any missing options at end */
function reconcileItems(value: string[], options: string[]): string[] {
  const present = new Set(value)
  const missing = options.filter((o) => !present.has(o))
  return [...value, ...missing]
}
